// FILE: src/i18n/labels.js
// Enum → words (plan WP-8 task 2, matrix 8.4 / 9.x clear language). Pages
// never print a stored enum (TECHNICAL, RESOLVED, LOGIN_FAILURE …); they
// call one of these. Unknown values are turned into sentence case rather
// than shown raw.

export function humanize(value) {
  if (value === null || value === undefined || value === "") return "—";
  const s = String(value).replace(/[_-]+/g, " ").trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const pick = (map) => (value) => map[value] || humanize(value);

export const ticketCategoryLabel = pick({
  TECHNICAL: "Technical support",
  SERVICE: "Student service support",
  CONTENT: "Course content support"
});

export const ticketCategoryHint = {
  TECHNICAL: "Problems signing in, loading lessons, video, sound or your device.",
  SERVICE: "Your account, enrolment, schedule, records or anything administrative.",
  CONTENT: "Questions about what the lessons teach. Answered by a registered driving instructor."
};

export const ticketStatusLabel = pick({ OPEN: "Open", RESOLVED: "Resolved", IN_PROGRESS: "In progress" });

// The account type. Staff accounts also have a staff role (RBAC), shown by its own name.
export const roleLabel = pick({ student: "Student", admin: "Staff" });

// NONE = the student has no licence yet (owner decision D11, 2026-10-07).
export const licenceClassLabel = pick({ G1: "G1 (learner)", G2: "G2 (probationary)", NONE: "No licence yet" });

export const resourceTypeLabel = pick({ CHAPTER: "Chapter", HOMELINK: "Homelink" });

/** What a lesson is (WP-5): core content, a graded assessment of a chapter, or a homelink. */
export const resourceKindLabel = pick({ CONTENT: "Lesson content", ASSESSMENT: "Graded assessment", HOMELINK: "Homelink" });

/** The activity on a slide (CD3.3c-d: quiz and matching are graded; interactive is not). */
export const challengeTypeLabel = pick({
  NONE: "No activity",
  INTERACTIVE: "Interactive activity (not graded)",
  QUIZ: "Quiz question (graded)",
  MATCHING: "Matching activity (graded)"
});

export const curriculumStatusLabel = pick({ DRAFT: "Draft", APPROVED: "Approved", RETIRED: "Retired" });

/** A curriculum check result (GET /api/admin/curricula/:id/validate). */
export const checkStatusLabel = pick({ PASS: "Passes", FAIL: "Fails", NOT_CHECKED: "Not checked" });

/** Activity-log actions, in the words a student would use. */
export const auditActionLabel = pick({
  LOGIN_SUCCESS: "Signed in",
  LOGIN_FAILURE: "Sign-in attempt failed",
  LOGIN_BLOCKED_LOCKED: "Sign-in refused: account locked",
  LOGOUT: "Signed out",
  OTP_SENT: "Verification code sent",
  SESSION_REPLACED: "Signed out: signed in on another device",
  SESSION_IDLE_TIMEOUT: "Signed out after 15 minutes without activity",
  USER_LOCKED: "Account locked",
  USER_UNLOCKED: "Account unlocked by support",
  USER_REGISTERED: "Account created",
  STUDENT_INVITED: "Invitation sent",
  INVITE_ACCEPTED: "Registration completed",
  PASSWORD_RESET_REQUESTED: "Password reset requested",
  PASSWORD_RESET: "Password reset",
  PASSWORD_CHANGED: "Password changed",
  PASSWORD_EXPIRED: "Password expired",
  CHALLENGE_ANSWERS_SET: "Security questions saved",
  CHALLENGE_ANSWERS_CHANGED: "Security questions changed",
  CHALLENGE_ANSWERS_CHANGE_REJECTED: "Security questions not changed: password check failed",
  IDENTITY_CHALLENGE_ISSUED: "Identity check started",
  IDENTITY_CHALLENGE_PASSED: "Identity check passed",
  IDENTITY_CHALLENGE_FAILED: "Identity check answer was wrong",
  TOTP_ENABLED: "Authenticator app turned on",
  TOTP_DISABLED: "Authenticator app turned off",
  TOTP_SETUP_STARTED: "Authenticator app setup started",
  TOTP_SETUP_REJECTED: "Authenticator app setup refused",
  TOTP_DISABLE_REJECTED: "Authenticator app not turned off: check failed",
  LEARNING_SESSION_STARTED: "Lesson opened",
  LEARNING_SESSION_ENDED: "Lesson closed",
  BREAK_REQUIRED: "Break needed after 2 hours",
  BREAK_STARTED: "Break started",
  BREAK_ENDED: "Break finished",
  DAILY_LIMIT_REACHED: "Reached the 5-hour daily limit",
  COMPLETION_REJECTED_MIN_TIME: "Lesson not complete yet: more time needed",
  RESOURCE_SCORE_RECORDED: "Quiz score recorded",
  RESOURCE_FINAL_SCORE_IMAGE: "Quiz result submitted",
  ENROLMENT_EXPIRED: "Enrolment period ended",
  ENROLMENT_EXTENDED: "Enrolment extended",
  SUPPORT_TICKET_CREATED: "Support request sent",
  SUPPORT_TICKET_UPDATE: "Support request updated",
  SUPPORT_TICKET_MESSAGE: "Message added to support request",
  PROFILE_UPDATED: "Profile updated",
  CONSENT_GIVEN: "Consent given",
  ADMIN_USER_UPDATED: "Details changed by an administrator",
  UNTIMED_GRANTED: "Untimed access granted",
  UNTIMED_REVOKED: "Untimed access removed",
  // WP-6 / WP-7 rows the admin audit log shows.
  REAUTH_FAILURE: "Password check failed",
  AUTH_FAILURE: "Sign-in check failed",
  USER_ENROLLED: "Enrolled by an administrator",
  INVITE_RESENT: "Invitation sent again",
  INVITE_EMAIL_FAILED: "Invitation email couldn't be sent",
  TUTORIAL_COMPLETED: "Course tutorial finished",
  DOCUMENT_ACKNOWLEDGED: "Document acknowledged",
  SURVEY_SUBMITTED: "Course survey sent",
  SUPPORT_TICKET_STATUS: "Support request status changed",
  ACCOMMODATION_REQUESTED: "Accommodation requested",
  ACCOMMODATION_UPDATED: "Accommodation request updated",
  RECORD_EXPORTED: "Student record downloaded",
  RECORDS_BULK_EXPORTED: "Student records exported",
  DAILY_HOURS_EXPORTED: "Daily hours report made",
  RESOURCE_CREATED: "Lesson added",
  RESOURCE_UPDATED: "Lesson changed",
  RESOURCE_DELETED: "Lesson deleted",
  CHAPTER_COMPLETED: "Chapter completed",
  HOMELINK_COMPLETED: "Homelink completed",
  CURRICULUM_CREATED: "Curriculum created",
  CURRICULUM_UPDATED: "Curriculum details changed",
  CURRICULUM_DELETED: "Draft curriculum deleted",
  CURRICULUM_VERSION_CREATED: "New curriculum version drafted",
  CURRICULUM_VALIDATED: "Curriculum checked against the Ministry rules",
  CURRICULUM_APPROVED: "Curriculum approved",
  CURRICULUM_RETIRED: "Curriculum retired",
  // Staff roles and permissions (RBAC).
  ROLE_CREATED: "Role created",
  ROLE_UPDATED: "Role changed",
  ROLE_DELETED: "Role deleted",
  STAFF_INVITED: "Staff member invited",
  STAFF_ROLE_CHANGED: "Staff role changed",
  PERMISSION_DENIED: "Refused: the role doesn't allow it"
});

/** Audit-row outcomes (WP-7): what happened to the attempt. */
export const auditOutcomeLabel = pick({ SUCCESS: "Succeeded", FAILURE: "Failed or refused", INFO: "Noted" });

/** Reason codes inside audit details. */
export const auditReasonLabel = pick({
  UNKNOWN_EMAIL: "unknown email address",
  NOT_REGISTERED: "registration not finished",
  BAD_PASSWORD: "wrong password",
  BAD_TOTP: "wrong authenticator code",
  MISSING_REAUTH: "password not given",
  MISSING_PASSWORD: "password not given",
  PASSWORD: "wrong password",
  OTP: "wrong verification code",
  TOTP: "wrong authenticator code",
  CHALLENGE: "wrong identity-check answer",
  MAX_PASSWORD_ATTEMPTS: "too many wrong passwords",
  MAX_OTP_ATTEMPTS: "too many wrong codes",
  MAX_TOTP_ATTEMPTS: "too many wrong authenticator codes",
  MAX_CHALLENGE_ATTEMPTS: "too many wrong identity-check answers",
  MAX_REAUTH_ATTEMPTS: "too many wrong passwords",
  NOT_ENABLED: "the authenticator app wasn't turned on"
});

/** Profile fields named in audit details and server validation problems. */
export const profileFieldLabel = pick({
  phone: "Phone number",
  "address.street": "Street address",
  "address.city": "City or town",
  "address.province": "Province or territory",
  "address.postalCode": "Postal code",
  address: "Address",
  hasConsent: "Consent",
  driverLicense: "Driver's licence"
});

export const userStatusLabel = pick({
  all: "All students",
  locked: "Locked",
  "in-progress": "In progress",
  "not-started": "Not started",
  completed: "Completed",
  invited: "Invitation sent",
  enrolled: "Enrolled",
  untimed: "Untimed (reviewer)",
  "not-enrolled": "Not enrolled",
  "enrolment-ended": "Enrolment ended"
});
